import { debugLog } from "./utils/debugLog.js";
import { sumSoftwareSavingsFromMatchedDiscounts } from "./utils/softwareSavingsTotals.js";
import { hasPublicReportWebsiteSearch } from "./utils/publicReportUrl.js";
import { softPatchKeyMetrics } from "./utils/keyMetricsApi.js";
import { computeGrantsAmount } from "./utils/keyMetricsCompute.js";
import React, { createContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import Airtable from 'airtable';

const AppContext = createContext();

const baseId = process.env.REACT_APP_AIR_BASEID;
const apiKey = process.env.REACT_APP_AIR_KEY;
const debugAppContext = process.env.REACT_APP_DEBUG_APP_CONTEXT === 'true';
const base = new Airtable({ apiKey: apiKey }).base(baseId);
const usersTable = process.env.REACT_APP_AIR_USERS || 'users';

// Retry Airtable fetch on 429 (rate limit) - exponential backoff, max 3 retries
const AIRTABLE_STAGGER_MS = 400;
const ensureFundingReportInflight = new Map();

function ensureFundingReport(domain) {
  const key = String(domain || '').trim().toLowerCase();
  if (!key) return Promise.resolve(null);
  if (!ensureFundingReportInflight.has(key)) {
    const request = fetch('/api/ensure-funding-report', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ domain: key }),
    }).finally(() => {
      ensureFundingReportInflight.delete(key);
    });
    ensureFundingReportInflight.set(key, request);
  }
  return ensureFundingReportInflight.get(key);
}

async function fetchWithRetry(fetchFn, retries = 3) {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      return await fetchFn();
    } catch (err) {
      const errStr = String(err?.message || err?.error || err || '');
      const isRetryable = err?.status === 429 || errStr.includes('429') || errStr.includes('Too Many Requests') || errStr.includes('rate');
      if (isRetryable && attempt < retries) {
        const delay = Math.min(2000 * Math.pow(2, attempt - 1), 15000);
        if (debugAppContext) console.warn(`[AppContext] Rate limit (429), retrying in ${delay}ms (attempt ${attempt}/${retries})...`);
        await new Promise((r) => setTimeout(r, delay));
      } else {
        throw err;
      }
    }
  }
}

function hasLessThanNWords(text, wordLimit) {
  if (!text) {
    return false;
  }
  // Split the text by spaces to count the words
  const words = text.trim().split(/\s+/);

  // Check if the number of words is less than the specified limit
  return words.length < wordLimit;
}

const AppProvider = ({ children }) => {
  const location = useLocation();
  const { getToken } = useAuth();
  const { user: clerkUserFromHook, isLoaded: isClerkLoaded } = useUser();
  const [company, setCompany] = useState();
  const [companies, setCompanies] = useState([]);
  const [offset, setOffset] = useState();
  const [showAmount, setShowAmount] = useState(0);

  // clerk
  const [clerkUser, setClerkUser] = useState(null);

  // airtable record
  const [userRecord, setUserRecord] = useState();

  const [domainStatus, setDomainStatus] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [grants, setGrants] = useState([]);
  const [grantLoading, setGrantLoading] = useState(true);
  const [grantAccess, setGrantAccess] = useState(null);
  const [grantCatalogCount, setGrantCatalogCount] = useState(0);
  const [matchedGrantCount, setMatchedGrantCount] = useState(0);
  const [matchedGrantTotal, setMatchedGrantTotal] = useState(0);
  const [calendarGrants, setCalendarGrants] = useState([]);
  const [grantReportProfile, setGrantReportProfile] = useState(null);
  const [JITWorking, setJITWorking] = useState(true);
  const [unknownDomain, setUnknownDomain] = useState(false);
  const [matchedDiscounts, setMatchedDiscounts] = useState([]);
  const [totalTechSavings, setTotalTechSavings] = useState(0);

  // Same total as SoftwareSavingsPage (dedupe by AssociatedTechnology, first wins; AnnualMargin then MaxSavings).
  useEffect(() => {
    setTotalTechSavings(sumSoftwareSavingsFromMatchedDiscounts(matchedDiscounts));
  }, [matchedDiscounts]);
  const [eligibleGrants, setEligibleGrants] = useState([]);
  const [grantsSum, setGrantsSum] = useState(0);
  const [flaggedGrants, setFlaggedGrants] = useState([]);

  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [pastAnswersContext, setPastAnswersContext] = useState([]);

  const [showTechPipelineInfo, setShowTechPipelineInfo] = useState(false);
  let [techScraperStatus, setTechScraperStatus] = useState(false);

  // Vendor Savings actions - exposed by UpgradeList, consumed by Layout
  const [vendorSavingsActions, setVendorSavingsActions] = useState({
    openAddExpenseModal: null,
    handleCsvUpload: null,
    csvUploadLoading: false,
    csvUploadFeedback: { show: false, message: '', type: 'info' },
  });

  // Pages that don't need the main data fetch
  // Add any paths you want to be considered "admin" or "no-data-fetch" pages here.
  // Note: Our new section pages (company-info-section, my-reports, etc.) DO need data, so they're not listed here
  const adminPages = [
    // Auth flows - no Airtable data needed
    '/sign-up',
    '/sign-in',
    '/workspace-selection',
    '/check_oauth_google',
    '/forgot-password',
    '/sso-callback',
    '/vendor-savings-admin',
    '/users-admin',
    '/customers-admin',
    '/admin/earnings-roadmap-email',
    '/admin/team-docs',
    '/admin/ar-ledger-log',
    '/data-cleanup',
    '/perfectdraft-data-cleanup',
    '/industry-matching',
    '/grants-admin',
    '/all-opportunities', // AllOpportunitiesBoard fetches its own data and doesn't need AppContext grants/companies
    '/enhanced-grants-form',
    '/edit-grant-questions',
    '/automation-success',
    '/all-grants',
    '/companies',
    '/leaderboard',
    '/prospect-leaderboard',
    '/my-network',
    '/successful-grant-writers',
    '/perks',
    '/pricing',
    '/financing-requests',
    '/financing-partners',
    '/automation-requests',
    '/settings',
    '/email-prototype',
    '/company-info-section',
    '/', // marketing home — no company/grants fetch
    '/software-savings',
    '/onboarding-v2',
    '/company-earnings-estimate',
    '/help',
    '/help-center',
    // REMOVED '/grants-section' - it needs grants data!
  ];

  // Pages that only need the single company, not all companies/grants
  const singleCompanyPages = [
    '/company-info-section',
    // '/ai-automations',
  ];

  // Pages that need grants but not all companies (optimized - fetch company first, then grants)
  const grantsPages = [
    '/grants-section',
  ];

  // Check if current page is an admin page that doesn't need data
  // Use exact match or pathname starts with the admin page path
  const isAdminPage = adminPages.some(page => {
    // Marketing home never needs company/grants fetch
    if (page === '/') {
      return location.pathname === '/';
    }
    // For other paths, check exact match or if pathname starts with the admin page path
    return location.pathname === page || location.pathname.startsWith(page + '/');
  });

  // Check if current page only needs single company (not all companies/grants)
  const isSingleCompanyPage = singleCompanyPages.some(page => {
    return location.pathname === page || location.pathname.startsWith(page + '/');
  });

  // Check if current page is a grants page (needs grants but not all companies)
  const isGrantsPage = grantsPages.some(page => {
    return location.pathname === page || location.pathname.startsWith(page + '/');
  });

  // Helper function to get query variable from URL
  const getQueryVariable = (variable) => {
    const query = location.search.substring(1);
    const vars = query.split('&');
    for (let i = 0; i < vars.length; i++) {
      const pair = vars[i].split('=');
      if (pair[0] === variable) {
        return decodeURIComponent(pair[1]);
      }
    }
    return false;
  };

  useEffect(() => {
    let cancelled = false;
    const contextFetchStartTime = Date.now();
    if (debugAppContext) {
      debugLog('🔄 [AppContext] Starting data fetch...', {
        pathname: location.pathname,
        search: location.search,
        isAdminPage,
        isSingleCompanyPage,
        isGrantsPage,
      });
    }

    // Home is a marketing page, but ?website=&company= is how admins scope a client
    // (same params as /company-info-section). Load just that company for the checklist widget.
    const websiteFromUrl = getQueryVariable('website');
    if (location.pathname === '/' && websiteFromUrl) {
      const fetchScopedHomeCompany = async () => {
        try {
          const escapedWebsite = String(websiteFromUrl)
            .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
            .replace(/'/g, "''");
          const companyName = getQueryVariable('company');
          const safeName = companyName ? String(companyName).replace(/'/g, "''") : '';
          const companyFormula = safeName
            ? `OR(REGEX_MATCH({Domain}, '^${escapedWebsite}$'), REGEX_MATCH({Domain}, '^www\\.${escapedWebsite}$'), {Name} = '${safeName}')`
            : `OR(REGEX_MATCH({Domain}, '^${escapedWebsite}$'), REGEX_MATCH({Domain}, '^www\\.${escapedWebsite}$'))`;

          const companyRecords = await fetchWithRetry(() =>
            base('companies')
              .select({
                view: 'Grid view',
                filterByFormula: companyFormula,
                maxRecords: 5,
              })
              .all()
          );
          if (cancelled) return;
          if (companyRecords?.length > 0) {
            const exactDomain = companyRecords.find(
              (c) =>
                c.fields?.Domain === websiteFromUrl ||
                c.fields?.Domain === `www.${websiteFromUrl}`
            );
            setCompany(exactDomain || companyRecords[0]);
          }
        } catch (err) {
          console.error('[AppContext] Error loading scoped home company:', err);
        } finally {
          if (!cancelled) {
            setLoading(false);
            setGrantLoading(false);
          }
        }
      };

      fetchScopedHomeCompany();
      return () => {
        cancelled = true;
      };
    }

    // Skip data fetching for admin pages
    if (isAdminPage) {
      if (debugAppContext) debugLog('⏭️ [AppContext] Skipping data fetch for admin page:', location.pathname);
      setLoading(false);
      setGrantLoading(false);
      return;
    }

    const websiteForReport = getQueryVariable('website');
    const isSummaryPageEarly = location.pathname.startsWith('/grants-section') && hasPublicReportWebsiteSearch(location.search);
    if ((isSummaryPageEarly || isGrantsPage) && websiteForReport && !isClerkLoaded) {
      return () => {
        cancelled = true;
      };
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        const website = getQueryVariable('website');
        const isSummaryPage = location.pathname.startsWith('/grants-section') && hasPublicReportWebsiteSearch(location.search);
        const isDashboardPage = isSummaryPage;

        // Public/company report: the API performs matching and only returns full
        // grant records when the authenticated viewer has an active entitlement.
        if ((isDashboardPage || isGrantsPage) && website) {
          if (debugAppContext) debugLog('📊 [AppContext] Dashboard: fetching protected grants report');
          const reportStarted = Date.now();
          setGrantReportProfile({ status: 'loading', startedAt: reportStarted });
          setCompanies([]);
          setQuestions([]);
          setAnswers([]);
          setGrantLoading(true);
          try {
            const token = clerkUserFromHook ? await getToken() : null;
            const reportHeaders = token ? { Authorization: `Bearer ${token}` } : {};
            let response = await fetch(`/api/grants-report?website=${encodeURIComponent(website)}`, {
              headers: reportHeaders,
            });
            if (response.status === 404) {
              const ensured = await ensureFundingReport(website);
              if (!ensured?.ok) {
                const ensureError = await ensured?.json?.().catch(() => ({}));
                throw new Error(ensureError?.error || 'Could not create funding report');
              }
              response = await fetch(`/api/grants-report?website=${encodeURIComponent(website)}`, {
                headers: reportHeaders,
              });
            }
            if (!response.ok) throw new Error(`Grants report request failed (${response.status})`);
            const report = await response.json();
            if (!cancelled) {
              setCompany(report.company);
              setGrants(report.grants || []);
              setEligibleGrants(report.grants || []);
              setCalendarGrants(report.calendarGrants || []);
              setGrantAccess(report.access || null);
              setGrantCatalogCount(report.catalogCount || 0);
              setMatchedGrantCount(report.matchedCount || 0);
              setMatchedGrantTotal(report.matchedTotal || 0);
              setGrantReportProfile({
                status: 'ready',
                clientMs: Date.now() - reportStarted,
                ...(report.profile || {}),
              });

              const grantsAmt = computeGrantsAmount(
                report.company?.fields,
                report.grants || []
              );
              if (grantsAmt != null && website) {
                softPatchKeyMetrics({
                  website,
                  getToken: async () => token,
                  patch: { grants: grantsAmt },
                });
              }
            }
          } catch (err) {
            if (!cancelled) {
              console.error('[AppContext] Protected grants report fetch failed:', err);
              setGrants([]);
              setEligibleGrants([]);
              setCalendarGrants([]);
              setGrantAccess(null);
              setGrantReportProfile({
                status: 'error',
                clientMs: Date.now() - reportStarted,
                error: err?.message || 'Grants report request failed',
              });
            }
          } finally {
            if (!cancelled) {
              setLoading(false);
              setGrantLoading(false);
            }
          }
          return;
        }

        // Questions/answers - skip for summary and grants pages
        const isSummaryOrGrantsPage = isSummaryPage || isGrantsPage;
        if (!isSummaryOrGrantsPage) {
          const questionsStartTime = Date.now();
          if (debugAppContext) debugLog('📋 [AppContext] Fetching questions and answers...');
          const [questionsRecords, answersRecords] = await Promise.all([
            base("questions").select({ view: "Grid view" }).all(),
            base("answers").select({ view: "Grid view" }).all(),
          ]);
          const questionsDuration = Date.now() - questionsStartTime;
          if (debugAppContext) debugLog(`✅ [AppContext] Questions/answers fetched in ${questionsDuration}ms`);
          setQuestions(questionsRecords);
          setAnswers(answersRecords);
        } else {
          if (debugAppContext) debugLog(`⏭️ [AppContext] Skipping questions/answers for ${isGrantsPage ? 'grants' : 'summary'} page`);
          setQuestions([]);
          setAnswers([]);
        }

        let companyData = null;

        if (isGrantsPage && website) {
          // Grants section: single company fetch (domain OR name) + grants
          const companyName = getQueryVariable('company');
          const escapedWebsite = String(website).replace(/'/g, "''").replace(/\./g, '\\.');
          const safeName = companyName ? String(companyName).replace(/'/g, "''") : '';
          const companyFormula = safeName
            ? `OR(REGEX_MATCH({Domain}, '^${escapedWebsite}$'), REGEX_MATCH({Domain}, '^www\\.${escapedWebsite}$'), {Name} = '${safeName}')`
            : `OR(REGEX_MATCH({Domain}, '^${escapedWebsite}$'), REGEX_MATCH({Domain}, '^www\\.${escapedWebsite}$'))`;

          const fetchStartTime = Date.now();
          const companyRecords = await fetchWithRetry(() =>
            base("companies").select({ view: "Grid view", filterByFormula: companyFormula, maxRecords: 5 }).all()
          );
          await new Promise((r) => setTimeout(r, AIRTABLE_STAGGER_MS));
          const grantsRecords = await fetchWithRetry(() => base("deduped_grants").select({ view: "Grid view" }).all());

          const fetchDuration = Date.now() - fetchStartTime;

          if (companyRecords && companyRecords.length > 0) {
            companyData = companyRecords[0];
            setCompany(companyData);
            setCompanies([]);
          } else {
            if (debugAppContext) console.warn(`[AppContext] Company not found for website: ${website}${companyName ? ` or name: ${companyName}` : ''}`);
          }
          setGrants(grantsRecords);
          if (companyData && grantsRecords.length > 0) {
            setEligibleGrants(grantsRecords);
          }
        } else if (!isSingleCompanyPage) {
          // For regular pages: fetch companies and grants in parallel
          const grantsStartTime = Date.now();
          if (debugAppContext) debugLog('📊 [AppContext] Fetching companies and grants in parallel...');
          // Stagger fetches to reduce Airtable rate limiting (429)
          const companiesRecords = await fetchWithRetry(() => base("companies").select({ view: "Grid view" }).all());
          await new Promise((r) => setTimeout(r, AIRTABLE_STAGGER_MS));
          const grantsRecords = await fetchWithRetry(() => base("deduped_grants").select({ view: "Grid view" }).all());
          const grantsDuration = Date.now() - grantsStartTime;
          if (debugAppContext) debugLog(`✅ [AppContext] Companies/grants fetched in ${grantsDuration}ms:`, {
            companies: companiesRecords.length,
            grants: grantsRecords.length,
          });
          
          setCompanies(companiesRecords);
          setGrants(grantsRecords);

          // Use company from already-fetched companies (no extra fetch)
          if (website) {
            const match = companiesRecords.find(
              (c) => c.fields?.Domain === website || c.fields?.Domain === `www.${website}`
            );
            if (match) {
              if (debugAppContext) debugLog('🏢 [AppContext] Company from companies list:', match.id);
              setCompany(match);
            } else if (debugAppContext) {
              console.warn(`⚠️ [AppContext] Company not found for website: ${website}`);
            }
          } else if (debugAppContext) {
            console.warn('⚠️ [AppContext] No website parameter found in URL');
          }
        } else {
          // For single-company pages, keep empty arrays to avoid breaking components
          if (debugAppContext) debugLog('⏭️ [AppContext] Skipping bulk companies/grants fetch for single-company page');
          setCompanies([]);
          setGrants([]);

          // Still fetch company if website provided
          if (website) {
            const companyStartTime = Date.now();
            if (debugAppContext) debugLog('🏢 [AppContext] Fetching company by website:', website);
            const companyRecords = await base("companies")
              .select({
                view: "Grid view",
                filterByFormula: `REGEX_MATCH({Domain}, '^${website}$')`,
              })
              .all();
            const companyDuration = Date.now() - companyStartTime;
            
            if (companyRecords && companyRecords.length > 0) {
              if (debugAppContext) debugLog(`✅ [AppContext] Company fetched in ${companyDuration}ms:`, {
                id: companyRecords[0].id,
                name: companyRecords[0].fields?.Name,
              });
              setCompany(companyRecords[0]);
            } else {
              if (debugAppContext) console.warn(`⚠️ [AppContext] Company not found for website: ${website} (took ${companyDuration}ms)`);
            }
          }
        }

        const totalDuration = Date.now() - contextFetchStartTime;
        if (debugAppContext) debugLog(`✅ [AppContext] Data fetch completed in ${totalDuration}ms`);
      } catch (err) {
        const totalDuration = Date.now() - contextFetchStartTime;
        console.error(`❌ [AppContext] Error occurred in fetching data after ${totalDuration}ms:`, err);
      } finally {
        setLoading(false); // Set loading to false after fetch completes (success or error)
        setGrantLoading(false); // Set grantLoading to false when companies data is loaded
      }
    };
  
    fetchData();
    return () => { cancelled = true; };
  }, [isAdminPage, isSingleCompanyPage, isGrantsPage, location.pathname, location.search, isClerkLoaded, clerkUserFromHook?.id, getToken]); // Include auth so entitlements refresh after sign-in

  // When URL has no website param: set company from signed-in user (Clerk metadata or Users → Companies), or clear for anonymous
  useEffect(() => {
    const website = getQueryVariable('website');
    if (website) return; // URL already has company context; main fetch sets company

    if (!isClerkLoaded) return;

    if (!clerkUserFromHook?.primaryEmailAddress?.emailAddress) {
      setCompany(undefined);
      return;
    }

    const resolveCompanyForSignedInUser = async () => {
      try {
        const meta = clerkUserFromHook.publicMetadata || {};
        const domainFromMeta = meta.company_domain;

        if (domainFromMeta) {
          const companyRecords = await base('companies')
            .select({
              view: 'Grid view',
              filterByFormula: `OR(LOWER({Domain}) = '${String(domainFromMeta).toLowerCase().replace(/'/g, "''")}', LOWER({Domain}) = 'www.${String(domainFromMeta).toLowerCase().replace(/'/g, "''")}')`,
            })
            .all();
          if (companyRecords?.length > 0) {
            setCompany(companyRecords[0]);
            return;
          }
        }

        const userEmail = clerkUserFromHook.primaryEmailAddress.emailAddress;
        const userRecords = await base(usersTable)
          .select({
            filterByFormula: `{Email} = '${userEmail.toLowerCase().replace(/'/g, "''")}'`,
            maxRecords: 1,
          })
          .all();

        if (userRecords?.length > 0) {
          const companiesField = userRecords[0].fields?.Companies;
          const companyIds = Array.isArray(companiesField) ? companiesField : (companiesField ? [companiesField] : []);
          const companyId = companyIds[0];
          if (companyId) {
            const companyRecord = await base('companies').find(companyId);
            if (companyRecord?.fields) {
              setCompany(companyRecord);
              return;
            }
          }
        }

        setCompany(undefined);
      } catch (err) {
        console.error('[AppContext] Error resolving company for signed-in user:', err);
        setCompany(undefined);
      }
    };

    resolveCompanyForSignedInUser();
  }, [isClerkLoaded, clerkUserFromHook?.id, clerkUserFromHook?.primaryEmailAddress?.emailAddress, clerkUserFromHook?.publicMetadata, location.search]);

  // Calculate eligibleGrants when company and grants are available (for grants pages)
  useEffect(() => {
    if (!isGrantsPage || !company || !grants.length) return;
    
    const eligibleGrantsStartTime = Date.now();
    if (debugAppContext) debugLog('🎯 [AppContext] Calculating eligibleGrants for grants page...', {
      companyName: company.fields?.Name,
      grantsCount: grants.length,
    });
    
    // TODO: Implement proper find_grants filtering here
    // For now, set to all grants - GrantsSectionPage will handle filtering
    // Proper solution: Move find_grants to shared utility or create server endpoint
    setEligibleGrants(grants);
    
    const duration = Date.now() - eligibleGrantsStartTime;
    if (debugAppContext) debugLog(`✅ [AppContext] EligibleGrants set in ${duration}ms (simplified - using all grants)`);
  }, [isGrantsPage, company, grants]);

  useEffect(() => {
    if (answers.length === 0 || questions.length === 0 || !company) return;
    let questionAndAnswers = [];

    const answersByCompany = answers
      .filter((answer) => answer.fields.CompanyID === company.id)
      .filter((answer) => hasLessThanNWords(answer.fields.AnswerText, 50));

    answersByCompany.forEach(answer => {
        // Find the corresponding question based on QuestionID
        const matchingQuestion = questions.find(question => question.fields.QuestionID === answer.fields.QuestionID);
        
        if (matchingQuestion) {
            questionAndAnswers.push({
                question: matchingQuestion.fields.QuestionText,
                answer: answer.fields.AnswerText
            });
        }
    });

    // debugLog("CONTEXT:", JSON.stringify(questionAndAnswers));
    setPastAnswersContext(questionAndAnswers);

}, [answers, company, questions]);

  // NOTE: eligibleGrants is calculated in App.js using find_grants()
  // If App.js is not mounted (e.g., on /grants-section route), eligibleGrants will be empty
  // GrantsSectionPage handles this by using grantsLocal as fallback
  // TODO: Move find_grants to a shared utility and calculate eligibleGrants here in AppContext

  return (
    <AppContext.Provider
      value={{
        company,
        setCompany,
        companies,
        setCompanies,
        offset,
        setOffset,
        showAmount,
        setShowAmount,
        clerkUser,
        setClerkUser,
        userRecord,
        setUserRecord,
        domainStatus,
        setDomainStatus,
        open,
        setOpen,
        loading,
        setLoading,
        grants,
        setGrants,
        grantLoading,
        setGrantLoading,
        grantAccess,
        setGrantAccess,
        grantCatalogCount,
        setGrantCatalogCount,
        matchedGrantCount,
        setMatchedGrantCount,
        matchedGrantTotal,
        setMatchedGrantTotal,
        calendarGrants,
        grantReportProfile,
        JITWorking,
        setJITWorking,
        unknownDomain,
        setUnknownDomain,
        matchedDiscounts,
        setMatchedDiscounts,
        totalTechSavings,
        setTotalTechSavings,
        eligibleGrants,
        setEligibleGrants,
        grantsSum,
        setGrantsSum,
        questions,
        setQuestions,
        answers,
        setAnswers,
        pastAnswersContext,
        setPastAnswersContext,
        showTechPipelineInfo,
        setShowTechPipelineInfo,
        techScraperStatus,
        setTechScraperStatus,
        vendorSavingsActions,
        setVendorSavingsActions,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export { AppProvider, AppContext };
